import React, { useState, useEffect } from "react";
import { Chip, useTheme } from "@mui/material";

interface CellChipStatusProps {
  /** any value; will be converted to string */
  children: any;
}

// Function to get custom colors based on theme
const getCustomColors = (theme: any): Record<string, string> => ({
  ACTIVE: theme.palette.primary.main,
  UNDER_NURTURING: theme.palette.primary.main,
  INACTIVE: theme.palette.error.main,
  ARCHIVED: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
  NOT_UNDER_NURTURING: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
  NOT_AVAILABLE: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
  SUCCESS: theme.palette.success.main,
  FAILURE: theme.palette.error.main,
  PENDING: theme.palette.warning.main,
  COMPLETED: theme.palette.primary.main,
  FAILED: theme.palette.error.main,
  RUNNING: theme.palette.info.main,
});

const formatLabel = (label: string) =>
  label
    .toLowerCase()
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const CellChipStatus: React.FC<CellChipStatusProps> = ({ children }) => {
  const theme = useTheme();
  const [runningText, setRunningText] = useState("Running");
  
  // convert any input to string
  const rawValue = String(children);
  // if string is empty or just whitespace, use fallback key
  const fallbackKey = 'NOT_AVAILABLE';
  const trimmed = rawValue.trim();
  const key = trimmed ? trimmed.toUpperCase() : fallbackKey;

  const customColors = getCustomColors(theme);
  const backgroundColor = customColors[key] || customColors.NOT_AVAILABLE;
  const formattedLabel = key === 'RUNNING' ? runningText : formatLabel(key);

  const greyTextStatuses = new Set([
    'ARCHIVED',
    'NOT_UNDER_NURTURING',
    'NOT_AVAILABLE',
  ]);

  // Get text color based on background brightness and theme
  const getTextColor = () => {
    if (greyTextStatuses.has(key)) {
      return theme.palette.text.primary;
    }
    // For colored backgrounds, use white text
    return theme.palette.common.white;
  };

  // Running text animation with setInterval
  useEffect(() => {
    if (key === 'RUNNING') {
      const runningStates = ["Running", "Running.", "Running..", "Running..."];
      let currentIndex = 0;
      
      const interval = setInterval(() => {
        currentIndex = (currentIndex + 1) % runningStates.length;
        setRunningText(runningStates[currentIndex]);
      }, 500); // Change every 500ms
      
      return () => clearInterval(interval);
    }
  }, [key]);

  return (
    <Chip
      label={formattedLabel}
      size="small"
      sx={{
        backgroundColor,
        color: getTextColor(),
        fontWeight: 500,
        ...(key === 'RUNNING' && {
          color: `${theme.palette.common.white} !important`,
          minWidth: '85px',
          width: 'auto',
          justifyContent: 'flex-start',
          '& .MuiChip-label': {
            textAlign: 'left',
            justifyContent: 'flex-start',
            paddingLeft: '12px',
            paddingRight: '12px',
          }
        })
      }}
    />
  );
};

export default CellChipStatus;
